// ---------------------------------------------------------------------------
// Intelligence derivations — pure functions that turn the raw report payloads
// into one-sentence headline insights for the Intelligence cluster.
//
// Each helper returns `{ headline, delta? }` so the view can render the
// primary signal on top and an optional delta line underneath. Helpers keep
// their own fallbacks and return `null` when the data is too thin to say
// anything useful (the component then shows a neutral "not enough data" note).
// ---------------------------------------------------------------------------

import type { TimeAllocationReport } from '../types/models';
import { intelligenceCopy } from '../copy/intelligence';

export interface HeadlineResult {
  headline: string;
  delta?: string;
}

// ---- Template interpolation --------------------------------------------

function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}

// ---- Capacity forecast --------------------------------------------------

export interface CapacityWeek {
  weekStart?: string;
  weekLabel?: string;
  availableMinutes?: number;
  meetingMinutes?: number;
  plannedMinutes?: number;
  utilisationPercent?: number;
}

function utilisation(week: CapacityWeek): number {
  if (week.utilisationPercent != null) return week.utilisationPercent;
  if ((week.availableMinutes ?? 0) > 0) {
    return Math.round(((week.plannedMinutes ?? 0) / (week.availableMinutes ?? 1)) * 100);
  }
  return 0;
}

export function deriveCapacityInsight(
  weeks: CapacityWeek[] | null | undefined,
): HeadlineResult | null {
  if (!weeks || weeks.length === 0) return null;

  const values = weeks.map(utilisation);
  const avg = Math.round(values.reduce((s, v) => s + v, 0) / values.length);

  const template =
    avg > 90
      ? intelligenceCopy.capacityOverloaded
      : avg >= 70
      ? intelligenceCopy.capacityBusy
      : intelligenceCopy.capacityHealthy;

  const headline = fill(template, { weeks: weeks.length, avg });

  // Delta: compare week 1 to peak week.
  let peakIdx = 0;
  for (let i = 1; i < weeks.length; i++) {
    if (values[i] > values[peakIdx]) peakIdx = i;
  }
  const peak = values[peakIdx];
  const deltaPoints = peak - values[0];
  const peakLabel = weeks[peakIdx].weekStart || weeks[peakIdx].weekLabel || `week ${peakIdx + 1}`;

  let delta: string;
  if (Math.abs(deltaPoints) < 5) {
    delta = intelligenceCopy.capacityDeltaSteady;
  } else if (deltaPoints > 0) {
    delta = fill(intelligenceCopy.capacityDeltaRise, {
      delta: deltaPoints,
      peakDate: peakLabel,
    });
  } else {
    delta = fill(intelligenceCopy.capacityPeakWeek, {
      date: peakLabel,
      peak,
    });
  }

  return { headline, delta };
}

// ---- Time allocation ----------------------------------------------------

function toHours(minutes: number): number {
  return Math.round((minutes / 60) * 10) / 10;
}

export function deriveTimeAllocationInsight(
  report: TimeAllocationReport | null | undefined,
): HeadlineResult | null {
  if (!report) return null;
  const total = report.totalMinutes ?? 0;
  if (total <= 0) return null;

  const meetingPct = Math.round(((report.totalMeetingMinutes ?? 0) / total) * 100);
  const focusPct = Math.round(((report.totalFocusMinutes ?? 0) / total) * 100);

  let headline: string;
  if (meetingPct > 60) {
    headline = fill(intelligenceCopy.timeAllocationMeetingHeavy, {
      meetingHours: toHours(report.totalMeetingMinutes ?? 0),
      focusHours: toHours(report.totalFocusMinutes ?? 0),
    });
  } else {
    headline = fill(intelligenceCopy.timeAllocationBalanced, {
      focusPct,
      meetingPct,
    });
  }

  // Delta: name the top driver and its share, if we have one.
  const topDriver = (report.byDriver ?? [])
    .filter((d) => d.driverName && d.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes)[0];

  const delta = topDriver
    ? fill(intelligenceCopy.timeAllocationTopDriver, {
        driverName: topDriver.driverName,
        driverPct: Math.round(topDriver.percentage),
      })
    : undefined;

  return { headline, delta };
}

// ---- Delegation ---------------------------------------------------------

export interface DelegationReportShape {
  totalDelegated: number;
  completed: number;
  completionRate: number;
  averageTurnaroundDays: number;
  byDelegatee?: Array<{ userId: string; name: string; received: number; completed: number; avgDays: number }>;
}

export function deriveDelegationInsight(
  report: DelegationReportShape | null | undefined,
): HeadlineResult | null {
  if (!report) return null;

  if (report.totalDelegated === 0) {
    return { headline: intelligenceCopy.delegationNone };
  }

  const headline = fill(intelligenceCopy.delegationActive, {
    total: report.totalDelegated,
    completionPct: Math.round(report.completionRate),
  });

  const delegatees = (report.byDelegatee ?? []).length;
  const delta =
    report.averageTurnaroundDays > 0 || delegatees > 0
      ? fill(intelligenceCopy.delegationTurnaround, {
          days: Math.round(report.averageTurnaroundDays * 10) / 10,
          delegatees,
        })
      : undefined;

  return { headline, delta };
}

// ---- Executive / My Reports --------------------------------------------

export interface ExecutiveInsightInput {
  totalPlanned: number;
  done: number;
  carried: number;
  longCarry: number; // carry-forward of 4+ days
}

export function deriveExecutiveInsight(
  input: ExecutiveInsightInput | null | undefined,
): HeadlineResult | null {
  if (!input) return null;
  if (input.totalPlanned === 0) {
    return { headline: intelligenceCopy.executiveEmpty };
  }

  const donePct = Math.round((input.done / input.totalPlanned) * 100);
  const headline = fill(intelligenceCopy.executiveOnTrack, {
    done: input.done,
    total: input.totalPlanned,
    donePct,
  });

  const delta =
    input.carried > 0
      ? fill(intelligenceCopy.executiveCarryForward, {
          carried: input.carried,
          longCarry: input.longCarry,
        })
      : undefined;

  return { headline, delta };
}
